import React from "react";
import { Link } from "react-router-dom";
import { Award, ArrowRight } from "lucide-react";

/**
 * CertificatePreviewCard — CollectorUp values certificates highly, so
 * the dashboard treats each real certificate as a small trophy card
 * rather than a plain link row. Subtle gold accent + a dedicated
 * "View Certificate" action; every field on this card comes straight
 * from `/dashboard-summary`, so nothing is fabricated or joined to
 * album_instances (which would be a false relationship).
 */
export default function CertificatePreviewCard({ cert }) {
  const albumTitle = cert.album_title || cert.player_name || cert.team_name || "Album Completion Certificate";
  const issued = cert.claimed_at || cert.issued_at || cert.completion_date;
  const issuedLabel = issued ? new Date(issued).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  }) : null;
  return (
    <Link
      to={`/dashboard/certificates/${cert.cert_number}`}
      data-testid={`dash-cert-${cert.cert_number}`}
      aria-label={`View certificate ${cert.cert_number}`}
      className="group relative flex flex-col rounded-2xl border-2 border-[var(--ink)] bg-gradient-to-br from-[var(--panini-yellow-soft)]/70 via-white to-white overflow-hidden shadow-[4px_4px_0_var(--ink)] hover:-translate-y-0.5 hover:shadow-[6px_6px_0_var(--ink)] transition-all focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--panini-yellow)]"
    >
      {/* Gold ribbon strip */}
      <div
        className="h-2 bg-gradient-to-r from-[var(--panini-yellow)] via-amber-400 to-[var(--panini-yellow)]"
        aria-hidden
      />
      <div className="p-5 md:p-6 flex flex-col gap-3 flex-1">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-12 h-12 rounded-xl bg-[var(--panini-yellow)] border-2 border-[var(--ink)] grid place-items-center shadow-[2px_2px_0_var(--ink)]">
            <Award size={20} aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--smoke)] font-bold">
              Album Completion Certificate
            </div>
            <div className="font-display text-lg md:text-xl leading-tight mt-0.5 line-clamp-2">
              {albumTitle}
            </div>
          </div>
        </div>
        <div className="text-xs text-[var(--ink)]/70 space-y-1 mt-1">
          <div className="font-mono truncate" title={cert.cert_number}>
            <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold mr-1">#</span>
            {cert.cert_number}
          </div>
          {issuedLabel && (
            <div>
              <span className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold mr-1">
                Issued
              </span>
              {issuedLabel}
            </div>
          )}
        </div>
        <div className="mt-auto pt-2 inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-[var(--ink)] group-hover:text-[var(--panini-red)] transition-colors">
          View Certificate <ArrowRight size={14} aria-hidden />
        </div>
      </div>
    </Link>
  );
}
